import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, fetchApiBlob } from "../../lib/api";
import { ResponsiveTable } from "../common/ResponsiveTable";
import ProjectView from "./ProjectView";
import HomeownerHubOverview from "./HomeownerHubOverview";
import { ChatCircleText, Copy, Files, Key, Plus } from "@phosphor-icons/react";
import type {
  HomeownerAction,
  HomeownerEdit,
  HomeownerPublication,
  HomeownerPublicationKind,
  HomeownerStaffData,
} from "../../types/homeowner";
const names: Record<HomeownerAction, string> = {
  settings: "Set up the homeowner page",
  publish: "Add an update or file",
  invite: "Create homeowner access link",
  revoke: "Revoke access",
  withdraw: "Withdraw publication",
  respond: "Reply to homeowner",
  repair: "Link maintenance repair",
  offer: "Share a change order or renewal",
};
const kinds: Record<HomeownerPublicationKind, string> = {
  update: "Project update",
  photo: "Original photo",
  document: "Document",
  warranty: "Warranty document",
  photo_report: "Photo report with annotations",
  invoice: "Issued invoice",
  proposal: "Proposal",
  contract: "Contract signing",
  maintenance: "Maintenance agreement",
  appointment: "Maintenance visit",
};
const errorText = (error: unknown) => {
  const data = (error as { responseData?: unknown })?.responseData;
  const messages = (value: unknown): string[] => {
    if (typeof value === "string") return [value];
    if (Array.isArray(value)) return value.flatMap(messages);
    if (value && typeof value === "object")
      return Object.values(value).flatMap(messages);
    return [];
  };
  return (
    messages(data).join(" ") || "This could not be completed. Please try again."
  );
};
const sectionStyle = {
  p: { xs: 2, md: 2.5 },
  gap: 1.5,
  minWidth: 0,
  boxShadow: "none",
};
function EmptySection({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ py: 2, px: 1, textAlign: "center" }}>
      <Box
        sx={{
          mx: "auto",
          mb: 1.5,
          width: 42,
          height: 42,
          display: "grid",
          placeItems: "center",
          borderRadius: "md",
          bgcolor: "background.level1",
          color: "text.secondary",
        }}
      >
        {icon}
      </Box>
      <Typography level="title-md">{title}</Typography>
      <Typography level="body-sm" sx={{ mt: 0.75, mx: "auto", maxWidth: 440 }}>
        {children}
      </Typography>
    </Box>
  );
}
const saveLabels: Record<HomeownerAction, string> = {
  settings: "Save page settings",
  publish: "Add to homeowner page",
  invite: "Create link & code",
  revoke: "Remove access",
  withdraw: "Remove from page",
  respond: "Save reply",
  repair: "Create repair item",
  offer: "Share for approval",
};

export default function HomeownerPanel({ jobId }: { jobId: string }) {
  const cache = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const key = ["homeowner-staff", jobId];
  const url = `/api/jobs/${jobId}/homeowner/`;
  const query = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get<HomeownerStaffData>(url)).data,
  });
  const [edit, setEdit] = useState<HomeownerEdit | null>(null);
  const [issued, setIssued] = useState<HomeownerStaffData["invitation"]>();
  const [preview, setPreview] = useState(false);
  const [status, setStatus] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const mutation = useMutation({
    mutationFn: async (value: HomeownerEdit) =>
      (
        await api.post<HomeownerStaffData>(`${url}actions/`, {
          action: value.action,
          revision: value.revision,
          data: value.values,
        })
      ).data,
    onSuccess: (data, submitted) => {
      cache.setQueryData(key, data);
      setEdit(null);
      const saved: Record<HomeownerAction, string> = {
        settings: data.enabled
          ? "Page settings saved. Only homeowners with an active link and code can open it."
          : "Page settings saved. The homeowner page is off.",
        publish: data.enabled
          ? "Added to the homeowner page."
          : "Added to the page. Turn the page on and give the homeowner access when you are ready.",
        invite:
          "Access link created. Share the link and code with the homeowner using the instructions below.",
        withdraw:
          "Removed from the homeowner page. The original job record is unchanged.",
        revoke: "This homeowner's access has been removed.",
        respond: "Your reply is saved on the homeowner page.",
        repair: "Repair item created in the selected maintenance visit.",
        offer: "Added to the homeowner page for approval.",
      };
      setStatus(saved[submitted.action]);
      setCopyStatus("");
      if (data.invitation) setIssued(data.invitation);
    },
  });
  const loadFile = useCallback(
    (item: HomeownerPublication) => fetchApiBlob(`${url}files/${item.id}/`),
    [url],
  );
  const data = query.data;
  const open = (
    action: HomeownerAction,
    values: HomeownerEdit["values"] = {},
  ) => {
    if (!data) return;
    setEdit({ action, revision: data.revision, values });
    mutation.reset();
  };
  const set = (name: string, value: string | number | boolean | null) =>
    setEdit((e) => (e ? { ...e, values: { ...e.values, [name]: value } } : e));
  const value = (name: string) => String(edit?.values[name] ?? "");
  const field = (
    label: string,
    name: string,
    type = "text",
    multiline = false,
    helper?: string,
  ) => (
    <FormControl key={name}>
      <FormLabel>{label}</FormLabel>
      {multiline ? (
        <Textarea
          minRows={3}
          value={value(name)}
          onChange={(e) => set(name, e.target.value)}
          slotProps={{ textarea: { "aria-label": label, maxLength: 4000 } }}
        />
      ) : (
        <Input
          type={type}
          value={value(name)}
          onChange={(e) => set(name, e.target.value)}
          slotProps={{
            input: {
              "aria-label": label,
              maxLength: type === "text" ? 255 : undefined,
            },
          }}
        />
      )}
      {helper && <FormHelperText>{helper}</FormHelperText>}
    </FormControl>
  );
  const choice = (
    label: string,
    name: string,
    options: { id: string; label: string }[],
    onSelect?: (id: string) => void,
  ) => (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select
        aria-label={label}
        value={value(name) || null}
        placeholder="Choose…"
        onChange={(_, v) => {
          set(name, v || "");
          onSelect?.(v || "");
        }}
      >
        {options.map((row) => (
          <Option key={row.id} value={row.id}>
            {row.label}
          </Option>
        ))}
      </Select>
    </FormControl>
  );
  const submit = () => {
    if (!edit || !data) return;
    const values = { ...edit.values };
    if (edit.action === "settings") {
      values.request_owner = values.request_owner
        ? Number(values.request_owner)
        : null;
      values.next_update = values.next_update || null;
      values.installed_on = values.installed_on || null;
    }
    if (edit.action === "invite") {
      values.contact = Number(values.contact);
      values.days = Number(values.days || 90);
    }
    if (edit.action === "publish" && !values.date) delete values.date;
    if (edit.action === "repair")
      values.appointment = Number(values.appointment);
    if (edit.action === "offer") {
      values.proposal = Number(values.proposal);
      if (values.purpose === "renewal") {
        values.maintenance_contract = Number(values.maintenance_contract);
      } else {
        delete values.maintenance_contract;
        delete values.renewal_end;
        delete values.renewal_fee;
        delete values.contract_version;
      }
    }
    mutation.mutate({ ...edit, values });
  };
  if (query.isLoading) return <Typography>Loading homeowner hub…</Typography>;
  if (query.error || !data)
    return (
      <Alert
        color="danger"
        endDecorator={
          <Button
            color="danger"
            variant="outlined"
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        }
      >
        The homeowner page could not be loaded. {errorText(query.error)}
      </Alert>
    );
  const openSettings = () =>
    open("settings", {
      enabled: data.enabled,
      title: data.title,
      introduction: data.introduction,
      phase: data.phase,
      next_update: data.next_update,
      installed_on: data.installed_on,
      materials: data.materials,
      care_notes: data.care_notes,
      request_owner: data.request_owner,
    });
  const openPublish = () =>
    open("publish", {
      kind: "update",
      source_id: "",
      title: "",
      note: "",
      reviewed: false,
    });
  const openInvite = () =>
    open("invite", { contact: "", days: 90, recipient_verified: false });
  const openOffer = () =>
    open("offer", {
      purpose: "change_order",
      proposal: "",
      source_version: "",
      title: "",
      note: "",
    });
  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(`${label} copied.`);
    } catch {
      setCopyStatus(
        "Copy is unavailable. Select the text above and copy it manually.",
      );
    }
  };
  const activeItems = data.publications.filter((row) => row.active);
  return (
    <Stack spacing={2.5} data-testid="homeowner-management">
      <HomeownerHubOverview
        data={data}
        now={now}
        refreshing={query.isFetching}
        onSettings={openSettings}
        onPublish={openPublish}
        onInvite={openInvite}
        onPreview={() => setPreview(true)}
        onRefresh={() => void query.refetch()}
      />
      {status && <Alert color="success">{status}</Alert>}
      {mutation.error && !edit && (
        <Alert color="danger">{errorText(mutation.error)}</Alert>
      )}
      {issued && (
        <Alert color="warning">
          <Stack spacing={1}>
            <Typography level="title-md">
              Next: share access with {issued.email}
            </Typography>
            <Typography>
              Nothing has been sent automatically. Copy the link into an email
              or text to this homeowner. Give them the access code separately,
              such as over the phone. They will also enter the email shown above
              to sign in. Save these details before closing; the code is shown
              only once.
            </Typography>
            <Typography level="title-sm">1. Share this project link</Typography>
            <Input
              value={`${window.location.origin}${issued.path}`}
              readOnly
              slotProps={{ input: { "aria-label": "New homeowner link" } }}
            />
            <Button
              variant="outlined"
              startDecorator={<Copy />}
              onClick={() =>
                void copy(
                  `${window.location.origin}${issued.path}`,
                  "Project link",
                )
              }
            >
              Copy project link
            </Button>
            <Typography level="title-sm">
              2. Give them this code separately
            </Typography>
            <Input
              value={issued.code}
              readOnly
              slotProps={{
                input: { "aria-label": "New homeowner access code" },
              }}
            />
            <Button
              variant="outlined"
              startDecorator={<Copy />}
              onClick={() => void copy(issued.code, "Access code")}
            >
              Copy access code
            </Button>
            {copyStatus && (
              <Typography role="status" level="body-sm">
                {copyStatus}
              </Typography>
            )}
            <Typography level="body-sm">
              Expires {new Date(issued.expires_at).toLocaleDateString()}.
              Issuing another invitation for this contact revokes the old one.
            </Typography>
            <Button variant="outlined" onClick={() => setIssued(undefined)}>
              Hide access details
            </Button>
          </Stack>
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1.65fr) minmax(0, 1fr)",
          },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          <Card sx={sectionStyle}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <Typography level="h4">
                On the homeowner page{" "}
                <Chip size="sm">{activeItems.length}</Chip>
              </Typography>
              {data.can_edit && (
                <Button
                  size="sm"
                  variant="outlined"
                  startDecorator={<Plus />}
                  onClick={openPublish}
                >
                  Add update or file
                </Button>
              )}
            </Stack>
            <Typography level="body-sm">
              Updates and job records you have chosen for this homeowner.
            </Typography>
            {!data.enabled && !!activeItems.length && (
              <Alert color="neutral">
                These items are prepared, but the page is off. Turn it on in
                page settings to make them available.
              </Alert>
            )}
            {!activeItems.length && (
              <EmptySection
                icon={<Files size={24} />}
                title="Give them a clear picture of the job"
              >
                Start with a quick update, such as “Your roof installation is
                scheduled.” Add photos and paperwork as the job moves forward.
              </EmptySection>
            )}
            {!!activeItems.length && (
              <Stack spacing={1.25}>
                {activeItems.map((row) => (
                  <Box
                    key={row.id}
                    sx={{
                      p: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "md",
                      bgcolor: "background.level1",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      gap={1.5}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          level="title-md"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          {row.title}
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 0.5 }}>
                          {kinds[row.kind]}
                        </Typography>
                        <Typography level="body-xs" sx={{ mt: 0.5 }}>
                          Added by {row.published_by} ·{" "}
                          {new Date(row.published_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      {data.can_edit && (
                        <Button
                          size="sm"
                          color="danger"
                          variant="plain"
                          sx={{ flexShrink: 0 }}
                          disabled={mutation.isPending}
                          onClick={() =>
                            mutation.mutate({
                              action: "withdraw",
                              revision: data.revision,
                              values: { id: row.id },
                            })
                          }
                        >
                          Remove from page
                        </Button>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
            <Divider />
            <Box component="details" sx={{ fontSize: "sm" }}>
              <Typography
                component="summary"
                level="title-sm"
                sx={{
                  cursor: "pointer",
                  listStyle: "none",
                  "&::before": {
                    content: '"+"',
                    display: "inline-block",
                    width: 20,
                    color: "primary.plainColor",
                  },
                  "details[open] > &::before": { content: '"−"' },
                }}
              >
                What will the homeowner see?
              </Typography>
              <Typography level="body-sm" sx={{ mt: 1 }}>
                Only the updates and records you add here. Shared files keep the
                version you selected; later file edits must be shared again.
                Invoice balances and maintenance visit dates stay up to date. To
                include photo markups, choose a photo report.
              </Typography>
            </Box>
            {data.can_edit && (
              <Box component="details" sx={{ fontSize: "sm" }}>
                <Typography
                  component="summary"
                  level="title-sm"
                  sx={{
                    cursor: "pointer",
                    listStyle: "none",
                    "&::before": {
                      content: '"+"',
                      display: "inline-block",
                      width: 20,
                      color: "primary.plainColor",
                    },
                    "details[open] > &::before": { content: '"−"' },
                  }}
                >
                  Need approval for extra work or a renewal?
                </Typography>
                <Typography level="body-sm" sx={{ mt: 1, mb: 1 }}>
                  Share an existing proposal as a change order or maintenance
                  renewal for the homeowner to review and sign.
                </Typography>
                <Button size="sm" variant="outlined" onClick={openOffer}>
                  Share change order or renewal
                </Button>
              </Box>
            )}
          </Card>
          <Card sx={sectionStyle}>
            <Typography level="h4">Questions & service requests</Typography>
            <Typography level="body-sm">
              When the homeowner asks for help on their page, it appears here
              with a follow-up task for your team.
            </Typography>
            <Stack spacing={2}>
              {data.requests.length ? (
                data.requests.map((row) => (
                  <Card
                    key={row.id}
                    variant="outlined"
                    sx={{ boxShadow: "none" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Typography level="title-lg">{row.subject}</Typography>
                      <Chip size="sm">{row.status.replaceAll("_", " ")}</Chip>
                    </Box>
                    <Typography level="body-sm">
                      {row.customer} ·{" "}
                      {new Date(row.created_at).toLocaleString()}
                    </Typography>
                    <Typography sx={{ whiteSpace: "pre-wrap" }}>
                      {row.message}
                    </Typography>
                    {row.response && (
                      <Alert color="success">{row.response}</Alert>
                    )}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {row.task_id && (
                        <Button
                          component={Link}
                          to={`/operations/tasks?task=${row.task_id}`}
                          variant="outlined"
                        >
                          Open follow-up task
                        </Button>
                      )}
                      {data.can_edit && (
                        <>
                          <Button
                            onClick={() =>
                              open("respond", {
                                id: row.id,
                                response: row.response,
                                complete: false,
                              })
                            }
                          >
                            Write a reply
                          </Button>
                          {row.kind === "service" && !row.repair_item_id && (
                            <Button
                              variant="outlined"
                              onClick={() =>
                                open("repair", {
                                  id: row.id,
                                  appointment: "",
                                  title: row.subject,
                                  description: row.message,
                                  severity: "yellow",
                                })
                              }
                            >
                              Link maintenance repair
                            </Button>
                          )}
                        </>
                      )}
                      {row.repair_item_id && (
                        <Chip>Maintenance repair #{row.repair_item_id}</Chip>
                      )}
                    </Box>
                  </Card>
                ))
              ) : (
                <EmptySection
                  icon={<ChatCircleText size={24} />}
                  title="No requests yet"
                >
                  Homeowner questions and service requests will appear here. You
                  can reply and keep track of follow-up work in one place.
                </EmptySection>
              )}
            </Stack>
          </Card>
        </Stack>
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          <Card sx={sectionStyle}>
            <Typography level="h4">Who can open this page?</Typography>
            <Typography level="body-sm">
              Each homeowner needs their own link, access code, and matching
              email address.
            </Typography>
            {!data.enabled && !!data.invitations.length && (
              <Alert color="neutral">
                The page is off. Existing links will not open it until you turn
                it back on.
              </Alert>
            )}
            {!data.invitations.length && (
              <EmptySection
                icon={<Key size={24} />}
                title="No access links yet"
              >
                Once your page is ready, create a link and give the code to your
                homeowner separately.
              </EmptySection>
            )}
            <Stack gap={1.5}>
              {data.invitations.map((row) => {
                const expired = new Date(row.expires_at).getTime() <= now;
                const access = row.revoked
                  ? "Removed"
                  : expired
                    ? "Expired"
                    : data.enabled
                      ? "Active"
                      : "Page is off";
                return (
                  <Box
                    key={row.id}
                    sx={{
                      p: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "md",
                      bgcolor: "background.level1",
                    }}
                  >
                    <Typography
                      level="title-sm"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {row.email}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1}
                      sx={{ mt: 1 }}
                    >
                      <Chip
                        size="sm"
                        color={access === "Active" ? "success" : "neutral"}
                      >
                        {access}
                      </Chip>
                      <Typography level="body-xs">
                        Expires {new Date(row.expires_at).toLocaleDateString()}
                      </Typography>
                    </Stack>
                    {data.can_edit && !row.revoked && (
                      <Button
                        size="sm"
                        variant="plain"
                        color="danger"
                        disabled={mutation.isPending}
                        sx={{ mt: 1 }}
                        onClick={() =>
                          mutation.mutate({
                            action: "revoke",
                            revision: data.revision,
                            values: { id: row.id },
                          })
                        }
                      >
                        Remove access
                      </Button>
                    )}
                  </Box>
                );
              })}
            </Stack>
            {data.can_edit && (
              <Button
                variant="outlined"
                disabled={
                  !data.enabled || !data.contacts.some((row) => row.email)
                }
                onClick={openInvite}
              >
                Create homeowner access link
              </Button>
            )}
            {!data.enabled && (
              <Typography level="body-xs">
                Start with “Set up homeowner page” above to turn the page on.
              </Typography>
            )}
            {!data.contacts.some((row) => row.email) && (
              <Typography level="body-sm">
                Add an email address to the job’s homeowner contact before
                creating a link.
              </Typography>
            )}
            <Box component="details">
              <Typography
                component="summary"
                level="title-sm"
                sx={{
                  cursor: "pointer",
                  listStyle: "none",
                  "&::before": {
                    content: '"+"',
                    display: "inline-block",
                    width: 20,
                    color: "primary.plainColor",
                  },
                  "details[open] > &::before": { content: '"−"' },
                }}
              >
                How do I remove access?
              </Typography>
              <Typography level="body-sm" sx={{ mt: 1 }}>
                Use Remove access next to a homeowner to close their access to
                this page and its files immediately. Separate proposal and
                contract links have their own access settings.
              </Typography>
            </Box>
          </Card>
        </Stack>
      </Box>
      {!!data.offers.length && (
        <Card sx={sectionStyle}>
          <Typography level="h4">Change orders & renewals</Typography>
          <ResponsiveTable
            rows={data.offers}
            getRowKey={(row) => String(row.id)}
            columns={[
              {
                key: "purpose",
                header: "Offer",
                mobileVariant: "header",
                render: (row) => (
                  <Button
                    component={Link}
                    to={`/crm/proposals/${row.proposal}`}
                    variant="plain"
                  >
                    {row.purpose.replaceAll("_", " ")} · Proposal #
                    {row.proposal}
                  </Button>
                ),
              },
              { key: "status", header: "Status", render: (row) => row.status },
              {
                key: "accepted_amount",
                header: "Accepted amount",
                render: (row) =>
                  row.accepted_amount
                    ? Number(row.accepted_amount).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })
                    : "Awaiting acceptance",
              },
              {
                key: "renewal_end",
                header: "Renewal through",
                render: (row) => row.renewal_end || "—",
              },
            ]}
          />
          <Typography level="body-sm">
            Change orders use the existing proposal signature and accepted
            paperwork. Renewal acceptance extends the selected maintenance
            agreement once. Invoice changes remain in Billing.
          </Typography>
        </Card>
      )}
      <Card>
        <Box component="details">
          <Typography component="summary" level="title-lg">
            Page activity ({data.history.length})
          </Typography>
          <Stack spacing={1} sx={{ mt: 2 }}>
            {!data.history.length && (
              <Typography level="body-sm">
                Changes to this page and its access links will appear here.
              </Typography>
            )}
            {data.history.map((row) => (
              <Box key={row.revision}>
                <Typography level="title-sm">
                  {row.kind.replaceAll("_", " ")} · {row.actor}
                </Typography>
                <Typography level="body-xs">
                  Revision {row.revision} · {new Date(row.at).toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Card>
      <Modal open={preview} onClose={() => setPreview(false)}>
        <ModalDialog
          aria-labelledby="homeowner-preview-title"
          sx={{
            width: "min(1100px, 96vw)",
            maxHeight: "92vh",
            overflowY: "auto",
            p: { xs: 1.5, md: 2.5 },
          }}
        >
          <ModalClose aria-label="Close homeowner preview" />
          <Typography id="homeowner-preview-title" level="h3" sx={{ pr: 4 }}>
            Homeowner preview
          </Typography>
          <Typography level="body-sm">
            This is how your page looks to the homeowner. Opening this preview
            does not turn it on or give anyone access.
          </Typography>
          <ProjectView data={data.preview} loadFile={loadFile} />
        </ModalDialog>
      </Modal>
      <Modal
        open={!!edit}
        onClose={() => {
          if (!mutation.isPending) setEdit(null);
        }}
      >
        <ModalDialog
          sx={{
            width: "min(680px,95vw)",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <ModalClose aria-label="Close dialog" />
          <Typography level="h3" sx={{ pr: 3 }}>
            {edit ? names[edit.action] : ""}
          </Typography>
          {edit && (
            <Stack spacing={2}>
              {edit.action === "settings" && (
                <>
                  <Typography level="body-sm">
                    Set up the page your homeowner will open. You can prepare it
                    while it is off, then turn it on when you are ready to
                    share.
                  </Typography>
                  <Card variant="soft" sx={{ boxShadow: "none" }}>
                    <Checkbox
                      label="Turn on the homeowner page"
                      checked={!!edit.values.enabled}
                      onChange={(e) => set("enabled", e.target.checked)}
                    />
                    <Typography level="body-sm">
                      Only people with an active access link and code can open
                      it. This does not send an invitation.
                    </Typography>
                  </Card>
                  <Typography level="title-lg">
                    Welcome & project progress
                  </Typography>
                  {field(
                    "Project name",
                    "title",
                    "text",
                    false,
                    "Shown at the top of the homeowner’s page. For example: Smith family roof replacement.",
                  )}
                  {field(
                    "Welcome message",
                    "introduction",
                    "text",
                    true,
                    "A short introduction from your team. Tell them what they can find here.",
                  )}
                  {choice("Current stage of the job", "phase", [
                    { id: "planning", label: "Planning" },
                    { id: "scheduled", label: "Scheduled" },
                    { id: "in_progress", label: "In progress" },
                    { id: "completed", label: "Completed" },
                  ])}
                  {field(
                    "Next update promised by (optional)",
                    "next_update",
                    "date",
                    false,
                    "The date you expect to give them another update. This does not schedule a message.",
                  )}
                  <Divider />
                  <Typography level="title-lg">
                    Roof details for their records
                  </Typography>
                  <Typography level="body-sm">
                    Optional details the homeowner can refer to after the job is
                    finished.
                  </Typography>
                  {field("Installation date", "installed_on", "date")}
                  {field(
                    "Materials installed",
                    "materials",
                    "text",
                    true,
                    "For example: Owens Corning Duration shingles, Estate Gray.",
                  )}
                  {field("Care instructions", "care_notes", "text", true)}
                  <Divider />
                  <Typography level="title-lg">Who follows up?</Typography>
                  {choice("Assign homeowner requests to", "request_owner", [
                    { id: "", label: "Use job owner" },
                    ...data.owners.map((row) => ({
                      id: String(row.id),
                      label: row.name,
                    })),
                  ])}
                  <Typography level="body-sm">
                    Questions and service requests create follow-up tasks for
                    this team member.
                  </Typography>
                </>
              )}
              {edit.action === "publish" && (
                <>
                  <Typography level="body-sm">
                    Write a quick update or select a file already saved on this
                    job. Only the item you choose will be added to the homeowner
                    page.
                  </Typography>
                  {!data.enabled && (
                    <Alert color="neutral">
                      The page is off. You can prepare content now; turn it on
                      when you are ready to share.
                    </Alert>
                  )}
                  {choice(
                    "What would you like to share?",
                    "kind",
                    Object.entries(kinds).map(([id, label]) => ({ id, label })),
                    () => set("source_id", ""),
                  )}
                  {value("kind") !== "update" &&
                    choice(
                      "Choose from this job",
                      "source_id",
                      data.sources[value("kind")] || [],
                      (id) => {
                        const selected = data.sources[value("kind")]?.find(
                          (row) => row.id === id,
                        );
                        if (selected) set("title", selected.label);
                      },
                    )}
                  {value("kind") !== "update" &&
                    !(data.sources[value("kind")] || []).length && (
                      <Alert color="neutral">
                        No eligible records of this type are available on this
                        job yet. Add or issue the record in its job tab first,
                        then return here.
                      </Alert>
                    )}
                  {field(
                    "Title the homeowner will see",
                    "title",
                    "text",
                    false,
                    "For example: Roof installation scheduled for Monday.",
                  )}
                  {field("Homeowner message", "note", "text", true)}
                  {value("kind") === "update" &&
                    field("Update date", "date", "date")}
                  <Checkbox
                    label="I reviewed this content for the homeowner"
                    checked={!!edit.values.reviewed}
                    onChange={(e) => set("reviewed", e.target.checked)}
                  />
                </>
              )}
              {edit.action === "invite" && (
                <>
                  {choice(
                    "Homeowner contact",
                    "contact",
                    data.contacts
                      .filter((row) => row.email)
                      .map((row) => ({
                        id: String(row.id),
                        label: `${row.name} · ${row.email}`,
                      })),
                  )}
                  {!data.contacts.some((row) => row.email) && (
                    <Alert color="warning">
                      Add an email address to the job’s homeowner contact before
                      creating access.
                    </Alert>
                  )}
                  {field(
                    "Link expires after (days)",
                    "days",
                    "number",
                    false,
                    "Choose 1–365 days. You can remove access at any time.",
                  )}
                  <Checkbox
                    label="I verified the homeowner identity and contact details"
                    checked={!!edit.values.recipient_verified}
                    onChange={(e) =>
                      set("recipient_verified", e.target.checked)
                    }
                  />
                  <Alert>
                    After creating access, copy the link into an email or text.
                    Give the code separately, such as over the phone. Nothing is
                    sent automatically.
                  </Alert>
                </>
              )}
              {edit.action === "respond" && (
                <>
                  {field(
                    "Reply visible to homeowner",
                    "response",
                    "text",
                    true,
                  )}
                  <Checkbox
                    label="Mark the follow-up task completed"
                    checked={!!edit.values.complete}
                    onChange={(e) => set("complete", e.target.checked)}
                  />
                </>
              )}
              {edit.action === "repair" && (
                <>
                  {choice(
                    "Maintenance appointment",
                    "appointment",
                    data.sources.appointment || [],
                  )}
                  {field("Repair title", "title")}
                  {field(
                    "Reviewed repair description",
                    "description",
                    "text",
                    true,
                  )}
                  {choice("Inspection priority", "severity", [
                    { id: "green", label: "Good condition" },
                    { id: "yellow", label: "Monitor / inspection needed" },
                    { id: "red", label: "Needs repair" },
                  ])}
                  <Typography level="body-sm">
                    This creates one repair item in the selected existing
                    maintenance visit. Review the homeowner report before
                    assigning a finding.
                  </Typography>
                </>
              )}
              {edit.action === "offer" && (
                <>
                  {choice("Offer purpose", "purpose", [
                    { id: "change_order", label: "Change order" },
                    { id: "renewal", label: "Maintenance renewal" },
                  ])}
                  {choice(
                    "Issued proposal",
                    "proposal",
                    data.sources.proposal || [],
                    (id) => {
                      const row = data.sources.proposal.find(
                        (item) => item.id === id,
                      );
                      set("source_version", row?.source_version || "");
                    },
                  )}
                  {field("Offer title", "title")}
                  {field(
                    "Reviewed scope and terms for homeowner",
                    "note",
                    "text",
                    true,
                  )}
                  {value("purpose") === "renewal" && (
                    <>
                      {choice(
                        "Existing maintenance agreement",
                        "maintenance_contract",
                        data.sources.maintenance || [],
                        (id) =>
                          set(
                            "contract_version",
                            data.sources.maintenance.find(
                              (row) => row.id === id,
                            )?.version || "",
                          ),
                      )}
                      {field("Renew coverage through", "renewal_end", "date")}
                      {field("Renewal fee per visit", "renewal_fee", "number")}
                    </>
                  )}
                  <Typography level="body-sm">
                    Review the linked proposal and put the same renewal dates
                    and fees in its terms. Acceptance uses the existing proposal
                    signature workflow.
                  </Typography>
                </>
              )}
              {mutation.error && (
                <Alert color="danger">{errorText(mutation.error)}</Alert>
              )}
              <Stack
                direction="row"
                justifyContent="flex-end"
                gap={1}
                sx={{
                  position: "sticky",
                  bottom: -16,
                  bgcolor: "background.surface",
                  py: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Button
                  variant="plain"
                  color="neutral"
                  disabled={mutation.isPending}
                  onClick={() => setEdit(null)}
                >
                  Cancel
                </Button>
                <Button
                  loading={mutation.isPending}
                  disabled={
                    (edit.action === "invite" &&
                      (!value("contact") || !edit.values.recipient_verified)) ||
                    (edit.action === "publish" &&
                      (!value("title").trim() ||
                        !edit.values.reviewed ||
                        (value("kind") !== "update" && !value("source_id"))))
                  }
                  onClick={submit}
                >
                  {saveLabels[edit.action]}
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
